/**
 * Account Queries
 *
 * React Query hooks for Account records (firm and individual).
 * useContactsByAccount returns contacts at a firm for the managing partner dropdown.
 * When shouldUseGobodo(), uses recordStorageService (Gobodo backend).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getRecordsForObject,
  getRecordById,
  createRecord,
  updateRecord,
  deleteRecord,
  type GenericRecord,
} from '../recordStorageService';
import { persistedAccountRecords } from '../mockData/accounts';
import { persistedContactRecords } from '../mockData/contacts';
import { persistedPricebookRecords } from '../mockData/pricebooks';
import * as stripePricebookService from '../stripePricebookService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import type { Account, AccountType } from '../../types/index';
import type { ListViewRecord } from '../../types/listView';
import type { Pricebook } from '../../types/index';

// --- Mappers (GenericRecord <-> Account) for Gobodo path ---

export function genericRecordToAccount(r: GenericRecord): Account {
  const typeRaw = r.type ?? (r as any).Type__c ?? 'firm';
  const type: AccountType = typeRaw === 'individual' ? 'individual' : 'firm';
  return {
    id: r.id,
    name: (r.name ?? r.label ?? (r as any).Name__c ?? '') as string,
    type,
    description: (r.description ?? (r as any).Description__c) as string | undefined,
    accountOwner: (r.accountOwner ?? (r as any).AccountOwner__c) as string | undefined,
    phone: (r.phone ?? (r as any).Phone__c) as string | undefined,
    email: (r.email ?? (r as any).Email__c) as string | undefined,
    billingAddress: (r.billingAddress ?? (r as any).BillingAddress__c) as string | undefined,
  };
}

export function accountToRecordData(
  a: Partial<Account> | Omit<Account, 'id'>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (a.name !== undefined) out.name = a.name;
  if (a.type !== undefined) out.type = a.type;
  if (a.description !== undefined) out.description = a.description;
  if (a.accountOwner !== undefined) out.accountOwner = a.accountOwner;
  if (a.phone !== undefined) out.phone = a.phone;
  if (a.email !== undefined) out.email = a.email;
  if (a.billingAddress !== undefined) out.billingAddress = a.billingAddress;
  return out;
}

/**
 * Get all accounts
 */
export function useAccounts() {
  return useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        const records = await getRecordsForObject('Account');
        return records.map(genericRecordToAccount);
      }
      await new Promise(resolve => setTimeout(resolve, 200));
      return persistedAccountRecords.get();
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get single account by ID
 */
export function useAccount(id: string | undefined) {
  return useQuery({
    queryKey: ['account', id],
    queryFn: async () => {
      if (!id) throw new Error('Account ID required');
      if (shouldUseGobodo()) {
        const record = await getRecordById('Account', id);
        if (!record) throw new Error('Account not found');
        return genericRecordToAccount(record);
      }
      await new Promise(resolve => setTimeout(resolve, 200));
      const accounts = persistedAccountRecords.get();
      const account = accounts.find(a => a.id === id);
      if (!account) throw new Error('Account not found');
      return account;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get contacts (persons) for an account/firm — for managing partner dropdown and Related Contacts
 */
export function useContactsByAccount(accountId: string | undefined) {
  return useQuery({
    queryKey: ['contactsByAccount', accountId],
    queryFn: async (): Promise<ListViewRecord[]> => {
      if (!accountId) return [];
      if (shouldUseGobodo()) {
        const records = await getRecordsForObject('Contact');
        const filtered = records.filter(
          r => (r.accountId ?? (r as any).AccountId__c ?? (r as any).AccountRelField__c) === accountId
        );
        return filtered.map(r => ({
          id: r.id,
          name: (r.name ?? r.label ?? (r as any).Name__c ?? '') as string,
          accountId: accountId,
          type: (r.type ?? (r as any).Type__c) as string | undefined,
          description: (r.description ?? (r as any).Description__c) as string | undefined,
          accountOwner: (r.accountOwner ?? (r as any).AccountOwner__c) as string | undefined,
          phone: (r.phone ?? (r as any).Phone__c) as string | undefined,
          email: (r.email ?? (r as any).Email__c) as string | undefined,
          billingAddress: (r.billingAddress ?? (r as any).BillingAddress__c) as string | undefined,
        })) as ListViewRecord[];
      }
      await new Promise(resolve => setTimeout(resolve, 200));
      const contacts = persistedContactRecords.get();
      return contacts.filter((c: ListViewRecord) => c.accountId === accountId);
    },
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get pricebook for a firm account (for "Current Price Book" on Firm detail).
 * Returns the firm's pricebook; if useStandardPricebook, returns the standard pricebook for display/link.
 * When shouldUseGobodo(), uses same source as usePricebooks (stripePricebookService.listPricebooks).
 */
export function usePricebookByAccount(accountId: string | undefined) {
  return useQuery({
    queryKey: ['pricebookByAccount', accountId],
    queryFn: async (): Promise<{ firmPricebook: Pricebook | null; displayPricebook: Pricebook } | null> => {
      if (!accountId) return null;
      const pricebooks = shouldUseGobodo()
        ? await stripePricebookService.listPricebooks()
        : (await new Promise(resolve => setTimeout(resolve, 200)), persistedPricebookRecords.get());
      const firmPricebook = pricebooks.find(pb => pb.accountId === accountId) ?? null;
      if (!firmPricebook) return null;
      if (firmPricebook.useStandardPricebook) {
        const standard = pricebooks.find(pb => pb.isStandard);
        if (!standard) return null;
        return { firmPricebook, displayPricebook: standard };
      }
      return { firmPricebook, displayPricebook: firmPricebook };
    },
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Create account mutation
 */
export function useCreateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (account: Omit<Account, 'id'>) => {
      if (shouldUseGobodo()) {
        const record = await createRecord('Account', accountToRecordData(account));
        return genericRecordToAccount(record);
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const newAccount: Account = {
        ...account,
        id: `acc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };
      persistedAccountRecords.add(newAccount);
      return newAccount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/**
 * Update account mutation
 */
export function useUpdateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Account> }) => {
      if (shouldUseGobodo()) {
        const record = await updateRecord('Account', id, accountToRecordData(updates));
        if (!record) throw new Error('Account not found');
        return genericRecordToAccount(record);
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const updated = persistedAccountRecords.update(id, updates);
      if (!updated) throw new Error('Account not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['account', data.id] });
    },
  });
}

/**
 * Delete account mutation (fails if pricebook or contacts reference this account)
 */
export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const pricebooks = shouldUseGobodo()
        ? await stripePricebookService.listPricebooks()
        : persistedPricebookRecords.get();
      if (pricebooks.some(pb => pb.accountId === id)) {
        throw new Error('Cannot delete account: linked to a pricebook');
      }
      if (shouldUseGobodo()) {
        const contactRecords = await getRecordsForObject('Contact');
        const hasRelated = contactRecords.some(
          r => (r.accountId ?? (r as any).AccountId__c ?? (r as any).AccountRelField__c) === id
        );
        if (hasRelated) {
          throw new Error('Cannot delete account: has related contacts');
        }
        await deleteRecord('Account', id);
        return id;
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const contacts = persistedContactRecords.get();
      if (contacts.some((c: ListViewRecord) => c.accountId === id)) {
        throw new Error('Cannot delete account: has related contacts');
      }
      const removed = persistedAccountRecords.remove(id);
      if (!removed) throw new Error('Account not found');
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}
